import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
    InputOTPSeparator,
} from "@/components/ui/input-otp";
import { 
    Shield, 
    Smartphone, 
    Lock, 
    Key, 
    QrCode,
    CheckCircle2,
    AlertCircle,
    Loader2,
    Copy,
    Check,
    RefreshCw,
    ShieldCheck,
    ShieldX,
    Eye,
    EyeOff,
    KeyRound,
    AlertTriangle,
    Download
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
    Dialog, 
    DialogContent, 
    DialogDescription, 
    DialogFooter, 
    DialogHeader, 
    DialogTitle 
} from "@/components/ui/dialog";
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
    setup2FA, 
    verify2FASetup, 
    disable2FA, 
    get2FAStatus, 
    regenerate2FABackupCodes 
} from '@/api';
import type { TwoFAStatusResponse } from '@/api/auth/auth.types';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';

export function SecuritySettings() {
    const { error, success } = useEnhancedToast();
    
    // State
    const [isLoading, setIsLoading] = useState(true);
    const [twoFAStatus, setTwoFAStatus] = useState<TwoFAStatusResponse | null>(null);
    
    // Setup flow state
    const [showSetupDialog, setShowSetupDialog] = useState(false);
    const [setupStep, setSetupStep] = useState<'qr' | 'verify' | 'backup'>('qr');
    const [qrCode, setQrCode] = useState<string>('');
    const [secret, setSecret] = useState<string>('');
    const [verificationCode, setVerificationCode] = useState('');
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [isSettingUp, setIsSettingUp] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    
    // Disable flow state
    const [showDisableDialog, setShowDisableDialog] = useState(false);
    const [disablePassword, setDisablePassword] = useState('');
    const [showDisablePassword, setShowDisablePassword] = useState(false);
    const [isDisabling, setIsDisabling] = useState(false);
    
    // Regenerate backup codes state
    const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [newBackupCodes, setNewBackupCodes] = useState<string[]>([]);
    
    // Copy states
    const [copiedSecret, setCopiedSecret] = useState(false);
    const [copiedBackupCodes, setCopiedBackupCodes] = useState(false);

    // Refs for toast functions
    const errorRef = useRef(error);
    const successRef = useRef(success);

    useEffect(() => {
        errorRef.current = error;
        successRef.current = success;
    }, [error, success]);

    // Fetch 2FA status on mount
    useEffect(() => {
        fetchStatus();
    }, []);

    const fetchStatus = async () => {
        setIsLoading(true);
        try {
            const response = await get2FAStatus();
            if (response.error) {
                throw new Error(response.error);
            }
            setTwoFAStatus(response.data || null);
        } catch {
            errorRef.current({
                icon: AlertCircle,
                title: "Failed to Load",
                description: "Could not fetch 2FA status. Please refresh the page.",
                duration: 4000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleStartSetup = async () => {
        setIsSettingUp(true);
        try {
            const response = await setup2FA();
            if (response.error) {
                throw new Error(response.error);
            }
            if (response.data) {
                setQrCode(response.data.qr_code);
                setSecret(response.data.secret);
                setSetupStep('qr');
                setShowSetupDialog(true);
            }
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Setup Failed",
                description: err instanceof Error ? err.message : "Could not initialize 2FA setup",
                duration: 4000,
            });
        } finally {
            setIsSettingUp(false);
        }
    };

    const handleVerifySetup = async () => {
        if (verificationCode.length !== 6 || !/^\d{6}$/.test(verificationCode)) {
            errorRef.current({
                icon: AlertCircle,
                title: "Invalid Code",
                description: "Please enter a valid 6-digit code",
                duration: 4000,
            });
            return;
        }

        setIsVerifying(true);
        try {
            const response = await verify2FASetup({ code: verificationCode });
            if (response.error) {
                throw new Error(response.error);
            }
            if (response.data) {
                setBackupCodes(response.data.backup_codes);
                setSetupStep('backup');
                successRef.current({
                    icon: ShieldCheck,
                    title: "2FA Enabled",
                    description: "Two-factor authentication has been activated",
                    duration: 4000,
                });
            }
        } catch (err) {
            errorRef.current({
                icon: ShieldX,
                title: "Verification Failed",
                description: err instanceof Error ? err.message : "Invalid verification code",
                duration: 4000,
            });
        } finally {
            setIsVerifying(false);
        }
    };

    const handleCompleteSetup = () => {
        setShowSetupDialog(false);
        setVerificationCode('');
        setQrCode('');
        setSecret('');
        setBackupCodes([]);
        setSetupStep('qr');
        fetchStatus();
    };

    const handleDisable2FA = async () => {
        if (!disablePassword) {
            errorRef.current({
                icon: AlertCircle,
                title: "Password Required",
                description: "Please enter your password to disable 2FA",
                duration: 4000,
            });
            return;
        }

        setIsDisabling(true);
        try {
            const response = await disable2FA({ password: disablePassword });
            if (response.error) {
                throw new Error(response.error);
            }
            successRef.current({
                icon: CheckCircle2,
                title: "2FA Disabled",
                description: "Two-factor authentication has been disabled",
                duration: 4000,
            });
            setShowDisableDialog(false);
            setDisablePassword('');
            fetchStatus();
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Failed to Disable",
                description: err instanceof Error ? err.message : "Could not disable 2FA",
                duration: 4000,
            });
        } finally {
            setIsDisabling(false);
        }
    };

    const handleRegenerateBackupCodes = async () => {
        setIsRegenerating(true);
        try {
            const response = await regenerate2FABackupCodes();
            if (response.error) {
                throw new Error(response.error);
            }
            if (response.data) {
                setNewBackupCodes(response.data.backup_codes);
                successRef.current({
                    icon: CheckCircle2,
                    title: "Backup Codes Regenerated",
                    description: "New backup codes have been generated",
                    duration: 4000,
                });
                fetchStatus();
            }
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Failed to Regenerate",
                description: err instanceof Error ? err.message : "Could not regenerate backup codes",
                duration: 4000,
            });
        } finally {
            setIsRegenerating(false);
        }
    };

    const handleCopySecret = () => {
        navigator.clipboard.writeText(secret);
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 2000);
    };

    const handleCopyBackupCodes = (codes: string[]) => {
        navigator.clipboard.writeText(codes.join('\n'));
        setCopiedBackupCodes(true);
        setTimeout(() => setCopiedBackupCodes(false), 2000);
    };

    const handleDownloadBackupCodes = (codes: string[]) => {
        const content = `Two-Factor Authentication Backup Codes
========================================
Save these codes in a secure location.
Each code can only be used once.

${codes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

Generated: ${new Date().toISOString()}
`;
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = '2fa-backup-codes.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <Skeleton className="h-8 w-8 rounded-lg" />
                            <div className="flex-1 space-y-2">
                                <Skeleton className="h-4 w-48" />
                                <Skeleton className="h-3 w-64" />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="space-y-4">
                            <Skeleton className="h-20 w-full" />
                            <Skeleton className="h-20 w-full" />
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const is2FAEnabled = twoFAStatus?.enabled && twoFAStatus?.verified;

    return (
        <div className="space-y-6">
            {/* 2FA Card */}
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <Shield className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <CardTitle>Two-Factor Authentication</CardTitle>
                                <CardDescription>Add an extra layer of security to your account</CardDescription>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {is2FAEnabled && (
                                <Badge variant="secondary" className="bg-green-500/10 text-green-600 hover:bg-green-500/20">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Enabled
                                </Badge>
                            )}
                            <Switch
                                id="2fa-toggle"
                                checked={is2FAEnabled}
                                onCheckedChange={(checked) => {
                                    if (checked) {
                                        handleStartSetup();
                                    } else {
                                        setShowDisableDialog(true);
                                    }
                                }}
                                disabled={isSettingUp}
                                className="scale-110"
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-6">
                    <div className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="flex items-center space-x-3 p-3 rounded-lg border bg-card">
                                <Smartphone className="h-5 w-5 text-muted-foreground" />
                                <div className="flex-1">
                                    <h4 className="text-sm font-medium">Authenticator App</h4>
                                    <p className="text-xs text-muted-foreground">Use Microsoft Authenticator, Google Authenticator, Authy, or similar</p>
                                </div>
                                {is2FAEnabled && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                            </div>

                            <div className="flex items-center space-x-3 p-3 rounded-lg border bg-card">
                                <Key className="h-5 w-5 text-muted-foreground" />
                                <div className="flex-1">
                                    <h4 className="text-sm font-medium">Backup Codes</h4>
                                    <p className="text-xs text-muted-foreground">
                                        {is2FAEnabled 
                                            ? `${twoFAStatus?.backup_codes_remaining || 0} codes remaining`
                                            : 'One-time use recovery codes'
                                        }
                                    </p>
                                </div>
                                {is2FAEnabled && twoFAStatus?.backup_codes_remaining !== undefined && (
                                    <Badge variant="outline" className={
                                        twoFAStatus.backup_codes_remaining <= 2 
                                            ? 'border-amber-500 text-amber-600' 
                                            : 'border-muted-foreground/30'
                                    }>
                                        {twoFAStatus.backup_codes_remaining}
                                    </Badge>
                                )}
                            </div>
                        </div>

                        {is2FAEnabled ? (
                            <div className="space-y-4">
                                {/* Status Info */}
                                <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                                    <div className="flex items-center gap-3">
                                        <ShieldCheck className="h-5 w-5 text-green-500" />
                                        <div>
                                            <p className="text-sm font-medium">2FA is active</p>
                                            {twoFAStatus?.enabled_at && (
                                                <p className="text-xs text-muted-foreground">
                                                    Enabled on {format(new Date(twoFAStatus.enabled_at), 'MMM d, yyyy')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {twoFAStatus?.last_used && (
                                        <p className="text-xs text-muted-foreground">
                                            Last used: {format(new Date(twoFAStatus.last_used), 'MMM d, yyyy h:mm a')}
                                        </p>
                                    )}
                                </div>

                                {/* Low backup codes warning */}
                                {twoFAStatus?.backup_codes_remaining !== undefined && twoFAStatus.backup_codes_remaining <= 2 && (
                                    <Alert variant="warning" className="bg-amber-50/50 dark:bg-amber-950/20">
                                        <AlertTriangle className="h-4 w-4" />
                                        <AlertTitle className="text-sm">Low Backup Codes</AlertTitle>
                                        <AlertDescription className="text-xs">
                                            You only have {twoFAStatus.backup_codes_remaining} backup code{twoFAStatus.backup_codes_remaining !== 1 ? 's' : ''} remaining. 
                                            Consider regenerating new codes.
                                        </AlertDescription>
                                    </Alert>
                                )}

                                {/* Regenerate Backup Codes Button */}
                                <div className="flex justify-end">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowRegenerateDialog(true)}
                                        className="gap-2"
                                    >
                                        <RefreshCw className="h-4 w-4" />
                                        Regenerate Backup Codes
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <Alert className="bg-muted/50">
                                <Lock className="h-4 w-4" />
                                <AlertDescription className="text-sm">
                                    Two-factor authentication adds an additional security layer by requiring a verification code along with your password when signing in.
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Setup Dialog */}
            <Dialog open={showSetupDialog} onOpenChange={(open) => {
                if (!open && setupStep !== 'backup') {
                    setShowSetupDialog(false);
                    setVerificationCode('');
                    setSetupStep('qr');
                }
            }}>
                <DialogContent className="sm:max-w-md p-0">
                    <DialogHeader className="border-b bg-muted/20 px-6 py-3">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                {setupStep === 'backup' ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                                ) : (
                                    <Shield className="h-4 w-4 text-primary" />
                                )}
                            </div>
                            <div className="flex-1 space-y-1">
                                <DialogTitle className="text-base font-semibold">
                                    {setupStep === 'qr' && 'Set Up Two-Factor Authentication'}
                                    {setupStep === 'verify' && 'Verify Your Authenticator'}
                                    {setupStep === 'backup' && '2FA Successfully Enabled'}
                                </DialogTitle>
                                <DialogDescription className="text-xs text-muted-foreground">
                                    {setupStep === 'qr' && 'Scan the QR code with your authenticator app'}
                                    {setupStep === 'verify' && 'Enter the 6-digit code from your app'}
                                    {setupStep === 'backup' && 'Save your backup codes in a secure location'}
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="px-6 py-4">
                        <AnimatePresence mode="wait">
                            {setupStep === 'qr' && (
                                <motion.div
                                    key="qr"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="space-y-4"
                                >
                                    {/* QR Code */}
                                    <div className="flex justify-center">
                                        <div className="p-3 bg-white rounded-xl border shadow-sm">
                                            {qrCode ? (
                                                <img 
                                                    src={`data:image/png;base64,${qrCode}`} 
                                                    alt="2FA QR Code" 
                                                    width={180}
                                                    height={180}
                                                    style={{ imageRendering: 'pixelated', display: 'block' }}
                                                />
                                            ) : (
                                                <div className="w-[180px] h-[180px] flex items-center justify-center">
                                                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Manual Entry */}
                                    <div className="space-y-2">
                                        <Label className="text-xs text-muted-foreground">
                                            Can't scan? Enter this code manually:
                                        </Label>
                                        <div className="flex items-center gap-2">
                                            <code className="flex-1 px-3 py-2 rounded-md bg-muted font-mono text-sm break-all border">
                                                {secret}
                                            </code>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="h-9 w-9 shrink-0"
                                                onClick={handleCopySecret}
                                            >
                                                {copiedSecret ? (
                                                    <Check className="h-4 w-4 text-green-500" />
                                                ) : (
                                                    <Copy className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </div>

                                    <Alert className="bg-muted/50">
                                        <QrCode className="h-4 w-4" />
                                        <AlertDescription className="text-xs">
                                            Use Microsoft Authenticator, Google Authenticator, Authy, 1Password, or any TOTP-compatible app.
                                        </AlertDescription>
                                    </Alert>
                                </motion.div>
                            )}

                            {setupStep === 'verify' && (
                                <motion.div
                                    key="verify"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="space-y-4"
                                >
                                    <div className="space-y-4">
                                        <Label className="flex items-center justify-center gap-2 text-sm font-medium">
                                            <KeyRound className="h-4 w-4 text-primary" />
                                            Verification Code
                                        </Label>
                                        <div className="flex justify-center">
                                            <InputOTP
                                                maxLength={6}
                                                value={verificationCode}
                                                onChange={(value) => setVerificationCode(value)}
                                                autoFocus
                                            >
                                                <InputOTPGroup>
                                                    <InputOTPSlot index={0} />
                                                    <InputOTPSlot index={1} />
                                                    <InputOTPSlot index={2} />
                                                </InputOTPGroup>
                                                <InputOTPSeparator />
                                                <InputOTPGroup>
                                                    <InputOTPSlot index={3} />
                                                    <InputOTPSlot index={4} />
                                                    <InputOTPSlot index={5} />
                                                </InputOTPGroup>
                                            </InputOTP>
                                        </div>
                                        <p className="text-xs text-muted-foreground text-center">
                                            Enter the 6-digit code from your authenticator app
                                        </p>
                                    </div>
                                </motion.div>
                            )}

                            {setupStep === 'backup' && (
                                <motion.div
                                    key="backup"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="space-y-4"
                                >
                                    <Alert variant="warning" className="bg-amber-50/50 dark:bg-amber-950/20">
                                        <AlertTriangle className="h-4 w-4" />
                                        <AlertTitle className="text-sm">Save These Codes</AlertTitle>
                                        <AlertDescription className="text-xs">
                                            Store these backup codes in a secure location. Each code can only be used once.
                                            You won't be able to see them again.
                                        </AlertDescription>
                                    </Alert>

                                    <div className="grid grid-cols-2 gap-2 p-4 bg-muted/50 rounded-lg border">
                                        {backupCodes.map((code, index) => (
                                            <code key={index} className="px-3 py-2 bg-background rounded text-sm font-mono text-center border">
                                                {code}
                                            </code>
                                        ))}
                                    </div>

                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="flex-1 gap-2"
                                            onClick={() => handleCopyBackupCodes(backupCodes)}
                                        >
                                            {copiedBackupCodes ? (
                                                <Check className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                            Copy All
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="flex-1 gap-2"
                                            onClick={() => handleDownloadBackupCodes(backupCodes)}
                                        >
                                            <Download className="h-4 w-4" />
                                            Download
                                        </Button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <DialogFooter className="border-t bg-muted/20 px-6 py-3">
                        {setupStep === 'qr' && (
                            <Button 
                                onClick={() => setSetupStep('verify')}
                                className="w-full sm:w-auto gap-2"
                                size="sm"
                            >
                                Continue
                            </Button>
                        )}
                        {setupStep === 'verify' && (
                            <div className="flex gap-2 w-full sm:w-auto">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSetupStep('qr')}
                                >
                                    Back
                                </Button>
                                <Button 
                                    onClick={handleVerifySetup}
                                    disabled={isVerifying || verificationCode.length !== 6}
                                    className="gap-2"
                                    size="sm"
                                >
                                    {isVerifying ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <ShieldCheck className="h-4 w-4" />
                                    )}
                                    Verify & Enable
                                </Button>
                            </div>
                        )}
                        {setupStep === 'backup' && (
                            <Button 
                                onClick={handleCompleteSetup}
                                className="w-full sm:w-auto gap-2"
                                size="sm"
                            >
                                <CheckCircle2 className="h-4 w-4" />
                                I've Saved My Codes
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Disable 2FA Dialog */}
            <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
                <DialogContent className="sm:max-w-md p-0">
                    <DialogHeader className="border-b bg-muted/20 px-6 py-3">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-destructive/10">
                                <ShieldX className="h-4 w-4 text-destructive" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <DialogTitle className="text-base font-semibold">Disable Two-Factor Authentication</DialogTitle>
                                <DialogDescription className="text-xs text-muted-foreground">
                                    Enter your password to confirm
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="px-6 py-4 space-y-4">
                        <Alert variant="destructive" className="bg-destructive/5 dark:bg-destructive/10 border-destructive/20">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertTitle className="text-sm">Warning</AlertTitle>
                            <AlertDescription className="text-xs">
                                Disabling 2FA will make your account less secure. You will only need your password to sign in.
                            </AlertDescription>
                        </Alert>

                        <div className="space-y-2">
                            <Label htmlFor="disable-password" className="flex items-center gap-2 text-sm font-medium">
                                <Lock className="h-4 w-4 text-primary" />
                                Password
                            </Label>
                            <div className="relative">
                                <Input
                                    id="disable-password"
                                    type={showDisablePassword ? "text" : "password"}
                                    placeholder="Enter your password"
                                    value={disablePassword}
                                    onChange={(e) => setDisablePassword(e.target.value)}
                                    className="h-9 pr-9"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowDisablePassword(!showDisablePassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showDisablePassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="border-t bg-muted/20 px-6 py-3">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setShowDisableDialog(false);
                                setDisablePassword('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleDisable2FA}
                            disabled={isDisabling || !disablePassword}
                            className="gap-2"
                        >
                            {isDisabling ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <ShieldX className="h-4 w-4" />
                            )}
                            Disable 2FA
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Regenerate Backup Codes Dialog */}
            <Dialog open={showRegenerateDialog} onOpenChange={(open) => {
                if (!open && newBackupCodes.length === 0) {
                    setShowRegenerateDialog(false);
                }
            }}>
                <DialogContent className="sm:max-w-md p-0">
                    <DialogHeader className="border-b bg-muted/20 px-6 py-3">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                {newBackupCodes.length > 0 ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                                ) : (
                                    <RefreshCw className="h-4 w-4 text-primary" />
                                )}
                            </div>
                            <div className="flex-1 space-y-1">
                                <DialogTitle className="text-base font-semibold">
                                    {newBackupCodes.length > 0 ? 'New Backup Codes Generated' : 'Regenerate Backup Codes'}
                                </DialogTitle>
                                <DialogDescription className="text-xs text-muted-foreground">
                                    {newBackupCodes.length > 0 
                                        ? 'Save these codes in a secure location'
                                        : 'This will invalidate all existing backup codes'
                                    }
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="px-6 py-4 space-y-4">
                        {newBackupCodes.length > 0 ? (
                            <>
                                <Alert variant="warning" className="bg-amber-50/50 dark:bg-amber-950/20">
                                    <AlertTriangle className="h-4 w-4" />
                                    <AlertTitle className="text-sm">Important</AlertTitle>
                                    <AlertDescription className="text-xs">
                                        Your old backup codes are now invalid. Save these new codes securely.
                                    </AlertDescription>
                                </Alert>

                                <div className="grid grid-cols-2 gap-2 p-4 bg-muted/50 rounded-lg border">
                                    {newBackupCodes.map((code, index) => (
                                        <code key={index} className="px-3 py-2 bg-background rounded text-sm font-mono text-center border">
                                            {code}
                                        </code>
                                    ))}
                                </div>

                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 gap-2"
                                        onClick={() => handleCopyBackupCodes(newBackupCodes)}
                                    >
                                        {copiedBackupCodes ? (
                                            <Check className="h-4 w-4 text-green-500" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                        Copy All
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 gap-2"
                                        onClick={() => handleDownloadBackupCodes(newBackupCodes)}
                                    >
                                        <Download className="h-4 w-4" />
                                        Download
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <Alert variant="warning" className="bg-amber-50/50 dark:bg-amber-950/20">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertTitle className="text-sm">Warning</AlertTitle>
                                <AlertDescription className="text-xs">
                                    Generating new backup codes will invalidate all your existing backup codes. 
                                    Make sure you have access to your authenticator app before proceeding.
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>

                    <DialogFooter className="border-t bg-muted/20 px-6 py-3">
                        {newBackupCodes.length > 0 ? (
                            <Button 
                                onClick={() => {
                                    setShowRegenerateDialog(false);
                                    setNewBackupCodes([]);
                                }}
                                className="w-full sm:w-auto gap-2"
                                size="sm"
                            >
                                <CheckCircle2 className="h-4 w-4" />
                                I've Saved My Codes
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowRegenerateDialog(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleRegenerateBackupCodes}
                                    disabled={isRegenerating}
                                    className="gap-2"
                                    size="sm"
                                >
                                    {isRegenerating ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <RefreshCw className="h-4 w-4" />
                                    )}
                                    Generate New Codes
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
